'use client';

import {Spinner} from '@components/Spinner';
import {useSignIn} from '@hooks/useSignIn';
import {isTenantSwitchRequested} from '@lib/tenant-switch';
import {useSession} from 'next-auth/react';
import {useRouter, useSearchParams} from 'next/navigation';
import type {FC} from 'react';
import {useLayoutEffect} from 'react';

import {LoginError, NextAuthError} from './_lib/errors';

const toLoginError = (errorParam: string): NextAuthError =>
  (Object.values(NextAuthError) as string[]).includes(errorParam)
    ? (errorParam as NextAuthError)
    : NextAuthError.Default;

const Login: FC = () => {
  const {data: session, status} = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();
  const {trigger: signIn, isMutating, reset} = useSignIn();

  // Auth0/next-auth bounced back with an error (e.g. the user isn't a member of any
  // organization → `error=Callback`). Auto-retrying just loops back to the same failure, so
  // we surface it via the login error boundary (below) instead of re-triggering sign-in.
  const errorParam = searchParams.get(`error`);

  useLayoutEffect(() => {
    // An error came back from the auth flow — don't re-trigger sign-in (we'd loop). The throw
    // below hands off to the error boundary; "Try again" clears the param and re-renders.
    if (errorParam) return () => {};
    // next-auth is still loading
    if (status === `loading`) return () => {};
    // We triggered the sign in action already
    if (isMutating) return () => reset();

    const params = Object.fromEntries(searchParams);
    // Remove 'callbackUrl' query param if it's 'logout' to avoid redirecting to the logout page
    if (params.callbackUrl === `/logout`) {
      delete params.callbackUrl;
    }

    // A tenant-scoped login link asks for a specific Auth0 organization. Honouring it requires a
    // round trip through Auth0, since the tenant is baked into the session claims - otherwise the
    // user silently stays in the tenant they were already signed in to. With nothing to switch to,
    // the middleware / root page.tsx redirects based on the user role.
    if (status === `authenticated` && !isTenantSwitchRequested(params, session?.tenant)) return () => {};

    signIn({params});

    return () => reset();
  }, [errorParam, isMutating, reset, router, status, signIn, searchParams, session]);

  if (errorParam) {
    throw new LoginError(toLoginError(errorParam));
  }

  return <Spinner />;
};

export default Login;
